import { StatExpandedResultResponse } from '@business/entities/dashboard';
import { R } from '@core/utils/r';
import 'chartist/dist/scss/chartist.scss';
import React, { memo, useMemo } from 'react';
import CountUp from 'react-countup';
import ScrollBar from 'react-perfect-scrollbar';
import { Card, CardBody, CardTitle, Col, Progress, Row } from 'reactstrap';
import { WidgetProps } from '../types';
import { WidgetIcons } from '../BaseWidget';

interface statExpandedItem {
  name: string;
  value: number;
  progress: number;
}

interface StatSingleLineProps {
  stat: statExpandedItem;
  label?: string;
  decimals: number;
}

const StatSingleLine = memo((props: StatSingleLineProps) => {
  const { stat } = props;
  return (
    <>
      <Row style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', marginBottom: 10 }}>
        <Col md={4}>
          <p>{stat.name}</p>
        </Col>
        <Col md={3}>
          <h5>
            <CountUp separator="," preserveValue end={stat.value} duration={1} decimals={props.decimals} prefix={props.label}></CountUp>
          </h5>
        </Col>
        <Col md={5}>
          <Progress color="info" value={stat.progress} className="font-size-12 mt-2" style={{ height: '5px' }}></Progress>
        </Col>
      </Row>
    </>
  );
});

export const StatExpandedWidget = memo((props: WidgetProps<StatExpandedResultResponse>) => {
  const { settings, result } = props;

  const stats = useMemo(() => {
    if (!result)
      return [
        {
          name: 'London',
          value: 1456,
          progress: 97.1,
        },
        {
          name: 'USA',
          value: 1123,
          progress: 74.8,
        },
        {
          name: 'GB',
          value: 1026,
          progress: 68.8,
        },
      ];
    return R.keys(result.items).map(key => ({
      name: key,
      value: R.get(result.items, key),
      progress: (R.get(result.items, key) / result.value) * 100,
    }));
  }, [result]);

  return (
    <Card className="p-4" style={{ marginBottom: 0, flex: 1 }}>
      <CardTitle className="mb-4 d-flex align-items-center flex-row">
        {settings.title || '# Stat (Expanded)'} <WidgetIcons {...props} />
      </CardTitle>
      <CardBody style={{ paddingTop: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div className="d-flex flex-column">
          <div style={{ textAlign: 'center' }}>
            <h3>
              <CountUp
                separator=","
                preserveValue
                end={!result ? 9999 : result.value}
                duration={1}
                decimals={result?.decimal || 0}
                prefix={result?.label || ''}
              ></CountUp>
            </h3>
          </div>
        </div>
        <div className="d-flex flex-fill mt-2" style={{ overflow: 'hidden' }}>
          <ScrollBar options={{ suppressScrollX: true }} style={{ width: '100%' }}>
            <div className="pr-2 pb-2 d-flex flex-column">
              {R.sortBy(stats, s => -s.value).map((stat, index) => {
                return <StatSingleLine key={index} stat={stat} decimals={result?.decimal || 0} label={result?.label || ''} />;
              })}
            </div>
          </ScrollBar>
        </div>
      </CardBody>
    </Card>
  );
});
